import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store';
import { fetchVanchiList, fetchVanchiStats } from '../../store/slices/vanchiSlice';
import StatsCard from '../../common/components/StatsCard';
import DataTable from '../../common/components/DataTable';
import { SimplePaginationInfo } from '../../types';
import WarningIcon from '../../common/assets/icons/warning.svg';
import SuccessIcon from '../../common/assets/icons/success.svg';
import ErrorIcon from '../../common/assets/icons/error.svg';

const index: React.FC = () => {
  const { t: _t } = useTranslation();
  const dispatch = useAppDispatch();
  const { list: vanchiList, stats, loading } = useAppSelector((state) => state.vanchi);
  const [currentPage, setCurrentPage] = useState(1);
  // const [sortColumn, setSortColumn] = useState<string | null>(null);
  // const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    dispatch(fetchVanchiList());
    dispatch(fetchVanchiStats());
  }, [dispatch]);


  const columns = [
    // Example: Auto-width column (remove width property to enable auto-width)
    // {
    //   key: 'sNo',
    //   label: 'S.No.',
    //   sortable: true,
    //   // width: 80, // Remove this line to enable auto-width
    // },
    // Note: DataTable automatically switches between:
    // - Fixed layout (tableLayout: 'fixed') when any column has width/minWidth/maxWidth
    // - Auto layout (tableLayout: 'auto') when no columns have width constraints
    {
      key: 'packsno',
      label: 'S.No.',
      sortable: true,
      width: 50,
      render: (_: any, __: any, rowIndex?: number, pagination?: any) => {
        const start = ((pagination?.page || 1) - 1) * (pagination?.limit || 0);
        return String((start + (rowIndex ?? 0) + 1)).padStart(2, '0');
      }
    },
    {
      key: 'packdt',
      label: 'Seal Date/Time',
      sortable: true,
      width: 160,
      render: (value: string | null) => {
        if (!value) return '-';
        try {
          const date = new Date(value);
          const formattedDate = date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: '2-digit'
          });
          const formattedTime = date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          });
          return `${formattedDate} / ${formattedTime}`;
        } catch (error) {
          return '-';
        }
      },
    },
    {
      key: 'transno',
      label: 'Trans No.',
      sortable: true,
      width: 100,
    },
    {
      key: 'nadacd',
      label: 'Nada',
      sortable: true,
      width: 80,
    },
    {
      key: 'vanchicd',
      label: 'Vanchi',
      sortable: true,
      width: 80,
    },
    {
      key: 'vanchino',
      label: 'Vanchi No',
      sortable: true,
      width: 90,
    },
    {
      key: 'bagno',
      label: 'Bag.No.',
      sortable: true,
      width: 60,
    },
    {
      key: 'packby',
      label: 'Sealed By',
      sortable: true,
      width: 100,
    },
    {
      key: 'secguard',
      label: 'Security Guard',
      sortable: true,
      width: 110,
    },
    { key: 'spacer', label: '' },
    {
      key: 'receivedt',
      label: 'Received Date/Time',
      sortable: true,
      width: 160,
      render: (value: string | null) => {
        if (!value) return '-';
        try {
          const date = new Date(value);
          const formattedDate = date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: '2-digit'
          });
          const formattedTime = date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          });
          return `${formattedDate} / ${formattedTime}`;
        } catch (error) {
          return '-';
        }
      },
    },
    {
      key: 'receiveby',
      label: 'Received By',
      sortable: true,
      width: 100,
      render: (value: string) => value || '-',
    },
    {
      key: 'packstatus',
      label: 'Status',
      sortable: true,
      width: 60,
      render: (value: any) => {
        const key = String(value || '').trim().toLowerCase();
        switch (key) {
          case 'in_transit':
            return <WarningIcon width={20} height={20} />;
          case 'received':
            return <SuccessIcon width={20} height={20} />;
          case 'rejected':
            return <ErrorIcon width={20} height={20} />;
          default:
            return <WarningIcon width={20} height={20} />;
        }
      },
    },
    {
      key: 'packcomments',
      label: 'Comments',
      sortable: false,
      width: 120,
      render: (value: string) => value || '-',
    },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSort = (_column: string, _direction: 'asc' | 'desc') => {
    // setSortColumn(column);
    // setSortDirection(direction);
    // In a real app, this would trigger a new API call with sort parameters
  };


  const pagination: SimplePaginationInfo = {
    page: currentPage,
    limit: 10,
  };

  return (
    <>
      {/* Stats Cards */}
      <div className="d-flex card-stats gap-4 mb-4">
        <div className="">
          <StatsCard
            title="Today"
            packedSent={stats.today.packedSent}
            received={stats.today.received}
            packedSentIcon={<WarningIcon width={15} height={15} />}
          />
        </div>
        <div className="">
          <StatsCard
            title="Yesterday"
            packedSent={stats.yesterday.packedSent}
            received={stats.yesterday.received}
            packedSentIcon={<SuccessIcon width={15} height={15} />}
          />
        </div>
        <div className="">
          <StatsCard
            title="This Week"
            packedSent={stats.week.packedSent}
            received={stats.week.received}
            packedSentIcon={<SuccessIcon width={15} height={15} />}
          />
        </div>
        <div className="">
          <StatsCard
            title="This Month"
            packedSent={stats.month.packedSent}
            received={stats.month.received}
            packedSentIcon={<SuccessIcon width={15} height={15} />}
          />
        </div>
        <div className="">
          <StatsCard
            title="This Year"
            packedSent={stats.year.packedSent}
            received={stats.year.received}
            packedSentIcon={<SuccessIcon width={15} height={15} />}
          />
        </div>
      </div>

      {/* Vanchi Status Table */}
      <div style={{ height: 'calc(100% - 120px)', backgroundColor: '#e9e6ec' }}>
        <DataTable
          columns={columns}
          data={vanchiList}
          loading={loading}
          pagination={pagination}
          onPageChange={handlePageChange}
          onSort={handleSort}
          searchable={false}
          totalCountOverride={useAppSelector((s) => s.vanchi.total)}
        />
      </div>
    </>
  );
};

export default index;
